import {
  getGuestSubmissionId,
  guestShareBase,
  isGuestShare,
} from 'utils/guestShare';

export enum ApiStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  COMPLETE = 'complete',

  FAILED = 'failed',
  FORBIDDEN = 'forbidden', // subtype of failed, when 403 status code is returned
}

// Auth Endpoints
const authControllerBaseAPI = '/auth';

export const forgotPasswordAPI = `${authControllerBaseAPI}/password/forgot`;
export const resetPasswordAPI = `${authControllerBaseAPI}/password/reset`;
export const requestPasswordlessSignInAPI = `${authControllerBaseAPI}/passwordless/request`;
export const verifyPasswordlessSignInAPI = `${authControllerBaseAPI}/passwordless/verify`;
export const userProfileAPI = `${authControllerBaseAPI}/profile`;

// Positions Endpoints
const posiitonsControllerBaseAPI = '/positions';

/** A guest reads the positions their grant covers, in the same shape, from the guest base. */
export const getPositionsAPI = () =>
  isGuestShare()
    ? `${guestShareBase()}/positions`
    : `${posiitonsControllerBaseAPI}/`;

// Candidates Endpoints
const candidatesControllerBaseAPI = '/candidates';

/**
 * A guest reaching the same page through a share link addresses the same actions under
 * /s/:token/submissions/:id. Swapping the base here keeps one code path through the thunks,
 * the modals and the reducers, so the guest page cannot drift from the page it mirrors.
 */
const candidateAPI = (id: string, suffix = '') =>
  isGuestShare()
    ? `${guestShareBase()}/submissions/${id}${suffix}`
    : `${candidatesControllerBaseAPI}/${id}${suffix}`;

export const getCandidateDetailsAPI = (id: string) => candidateAPI(id);
export const postCandidateFeedbackAPI = (id: string) =>
  candidateAPI(id, '/feedback');

export const cancelCandidateInterviewAPI = (id: string) =>
  candidateAPI(id, '/interview/cancel');

export const rescheduleCandidateInterviewAPI = (id: string) =>
  candidateAPI(id, '/interview/reschedule');

export const postInterviewFeedbackAPI = (id: string) =>
  candidateAPI(id, '/interview/feedback');

export const getCandidateSummaryViewAPI = (id: string) =>
  `${candidatesControllerBaseAPI}/summary-view/${id}`;

export const getCandidateVideoInterviewUrl = () =>
  isGuestShare()
    ? candidateAPI(getGuestSubmissionId() ?? '', '/video-interview/url')
    : `${candidatesControllerBaseAPI}/video-interview/url`;

export const sendCandidateQuestionAPI = (id: string) =>
  candidateAPI(id, '/questions');

export const guestGrantAPI = () => `${guestShareBase()}/grant`;
export const guestRequestCodeAPI = () => `${guestShareBase()}/request-code`;
export const guestVerifyCodeAPI = () => `${guestShareBase()}/verify`;
export const guestRequestAccessAPI = () => `${guestShareBase()}/request-access`;
export const guestStatusAPI = (active: boolean) =>
  `${guestShareBase()}/status?active=${active ? '1' : '0'}`;

// Sharing Endpoints
const shareControllerBaseAPI = '/share';

export const shareContextAPI = (submissionId: string) =>
  `${shareControllerBaseAPI}/context?submission=${encodeURIComponent(submissionId)}`;
export const shareAPI = shareControllerBaseAPI;
/** An access request a delivery manager decides on, and where the decision goes. */
export const shareRequestAPI = (requestId: string) =>
  `${shareControllerBaseAPI}/requests/${encodeURIComponent(requestId)}`;
export const shareRequestDecisionAPI = (
  requestId: string,
  decision: 'approve' | 'decline',
) => `${shareRequestAPI(requestId)}/${decision}`;

// User Notification Settings Endpoints
const userNotificationSettingsBaseAPI = '/user-notification-settings';

export const unsubscribeFromNotificationsAPI = () =>
  `${userNotificationSettingsBaseAPI}/unsubscribe`;

// Teams Endpoints
export const resubscribeToNotificationsAPI = () =>
  `${userNotificationSettingsBaseAPI}/resubscribe`;

export const fetchNotificationStatusAPI = (email: string) =>
  `${userNotificationSettingsBaseAPI}/status?email=${encodeURIComponent(email)}`;

const teamsBaseApi = '/teams';

export const getTeamsAPI = () => `${teamsBaseApi}`;

export const getTeamMemberAPI = (teamId: string, memberId: string) =>
  `${teamsBaseApi}/${teamId}/members/${memberId}`;

export const getAccountPermissionsAPI = () => `${teamsBaseApi}/permissions`;

export const getAccountImpersonationAPI = () => `${teamsBaseApi}/impersonation`;
